/**
 * La tarjeta de un inmueble en Ingresos y gastos: su nombre y un «+». Nada más.
 *
 * ES UNA CASILLA PARA SOLTAR TICKETS. Se arrastran los ficheros encima (a cualquier parte de la
 * tarjeta) o se pulsa y se eligen; van a la cola de subida (`UploadQueue.tsx`) y cada uno se
 * apunta en el mes de su fecha. Se puede seguir soltando mientras la cola sube: la tarjeta no se
 * bloquea. Las cifras y el estado de cada movimiento están en la tabla de conciliación de arriba:
 * repetirlos aquí convertía la tarjeta en un resumen y escondía para qué sirve.
 *
 * Lo único que se avisa es si al inmueble le falta algo este mes: un punto en la esquina.
 *
 * De fondo, la foto de portada del piso (velada para que el nombre se lea): se reconoce cada
 * tarjeta de un vistazo, sin leer.
 *
 * `MultiPropertyCard` es la misma casilla SIN PISO: para soltar de golpe las facturas de luz, agua
 * o wifi de varios pisos. De cada una, el piso lo dice el nombre del fichero o la propia factura
 * (su CUPS, su dirección); si nada lo dice, la cola lo pregunta.
 */
import { useRef, useState, type DragEvent } from 'react';
import type { LedgerProperty } from '@naar/shared';
import { Icon } from '../../components/Icon';
import { formatEurCents } from '../../lib/format';
import type { PropertyCover } from '../../lib/usePropertyCovers';
import { LEDGER_ACCEPT, useLedgerUpload } from './useLedgerUpload';
import styles from './PropertyCards.module.css';

interface PropertyCardProps {
  property: LedgerProperty;
  canManage: boolean;
  /** La foto de portada, si el piso tiene. */
  cover?: PropertyCover | undefined;
  /**
   * `franja`: a todo el ancho, con el texto a la izquierda y el «+» a la derecha. Es la forma de
   * GASTOS GENERALES, que no es un piso y no debe parecerlo.
   */
  layout?: 'tarjeta' | 'franja';
}

/** Solo reacciona a ficheros: arrastrar un texto o un enlace por encima no es soltar un ticket. */
function traeFicheros(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes('Files');
}

/** Soltar ficheros encima de una casilla: lo comparten la tarjeta de un piso y la de varios. */
function useSoltar(canManage: boolean, subir: (files: File[]) => void) {
  /*
   * Un contador y no un booleano: cada hijo de la tarjeta dispara su propio `dragenter` y
   * `dragleave`, y con un booleano el resaltado parpadea al pasar por encima del nombre.
   */
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const dropHandlers = canManage
    ? {
        onDragEnter: (event: DragEvent) => {
          if (!traeFicheros(event)) return;
          event.preventDefault();
          depth.current += 1;
          setOver(true);
        },
        onDragOver: (event: DragEvent) => {
          if (!traeFicheros(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: () => {
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setOver(false);
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault();
          depth.current = 0;
          setOver(false);
          subir(Array.from(event.dataTransfer.files));
        },
      }
    : {};
  return { over, dropHandlers };
}

/** Lo que dice la casilla debajo del «+»: cuántos quedan en cola, o cómo se sube. */
function Pista({ over, enCola, toque }: { over: boolean; enCola: number; toque: string }) {
  return (
    <span className={styles.hint}>
      {over ? (
        'Suéltalos para subirlos'
      ) : enCola > 0 ? (
        `${enCola} en cola · puedes soltar más`
      ) : (
        <>
          {/* Con ratón se arrastra; con el dedo no hay nada que arrastrar: se pulsa y se
              eligen las fotos o los PDF (o se hace la foto al momento). */}
          <span className={styles.hintPointer}>Arrastra tickets o pulsa</span>
          <span className={styles.hintTouch}>{toque}</span>
        </>
      )}
    </span>
  );
}

export function PropertyCard({ property, canManage, cover, layout = 'tarjeta' }: PropertyCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { subir, enCola } = useLedgerUpload({ id: property.id, name: property.name });
  const { over, dropHandlers } = useSoltar(canManage, subir);
  const faltan = property.gaps.length;
  const franja = layout === 'franja';

  return (
    <div
      className={
        franja
          ? [styles.band, over ? styles.bandOver : ''].filter(Boolean).join(' ')
          : [styles.card, over ? styles.cardOver : '', cover?.url ? styles.cardPhoto : ''].filter(Boolean).join(' ')
      }
      {...dropHandlers}
    >
      {cover?.url && (
        <span
          className={styles.photo}
          style={{
            backgroundImage: `url("${cover.url}")`,
            ...(cover.placeholderColor ? { backgroundColor: cover.placeholderColor } : {}),
          }}
          aria-hidden="true"
        />
      )}
      {canManage && (
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={LEDGER_ACCEPT}
          className={styles.input}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            subir(Array.from(event.target.files ?? []));
            // Se limpia para que volver a elegir el MISMO fichero dispare el evento otra vez.
            event.target.value = '';
          }}
        />
      )}
      {franja ? (
        <button
          type="button"
          className={styles.bandFace}
          onClick={() => inputRef.current?.click()}
          disabled={!canManage}
          aria-label={`Subir tickets de ${property.name}`}
        >
          <span className={styles.bandIcon} aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" />
              <path d="M9 7h6M9 11h6M9 15h4" />
            </svg>
          </span>
          <span className={styles.bandText}>
            <span className={styles.bandName}>
              {property.name}
              {faltan > 0 && (
                <span className={styles.bandFlag}>
                  <span className={styles.flagDot} aria-hidden="true" />
                  {faltan === 1 ? '1 pendiente' : `${faltan} pendientes`}
                </span>
              )}
            </span>
            <span className={styles.bandDesc}>
              Gestoría, comisiones del banco, seguros, software… lo que no es de ningún inmueble.
            </span>
          </span>
          <span className={styles.bandTotal}>
            <span className={styles.bandTotalLabel}>Este mes</span>
            <span className={styles.bandTotalValue}>{formatEurCents(property.expenses)}</span>
          </span>
          <span className={styles.bandDrop}>
            <span className={styles.plus} aria-hidden="true">
              <Icon name={over || enCola > 0 ? 'upload' : 'plus'} size={20} strokeWidth={2} />
            </span>
            <Pista over={over} enCola={enCola} toque="Pulsa para subir tickets" />
          </span>
        </button>
      ) : (
      <button
        type="button"
        className={styles.face}
        onClick={() => inputRef.current?.click()}
        disabled={!canManage}
        aria-label={`Subir tickets de ${property.name}`}
        title={
          faltan > 0
            ? `${property.name}: ${faltan === 1 ? 'falta 1 cosa' : `faltan ${faltan} cosas`} este mes`
            : property.name
        }
      >
        {faltan > 0 && (
          <span className={styles.flag}>
            <span className={styles.flagDot} aria-hidden="true" />
            {faltan === 1 ? '1 pendiente' : `${faltan} pendientes`}
          </span>
        )}
        <span className={styles.name}>{property.name}</span>
        <span className={styles.plus} aria-hidden="true">
          <Icon name={over || enCola > 0 ? 'upload' : 'plus'} size={20} strokeWidth={2} />
        </span>
        <Pista over={over} enCola={enCola} toque="Pulsa para subir tickets" />
      </button>
      )}
    </div>
  );
}

/** Soltar facturas de varios pisos a la vez: el piso de cada una sale de su nombre o de ella. */
export function MultiPropertyCard({
  pisos,
  canManage,
}: {
  pisos: { id: string; name: string }[];
  canManage: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { subir, enCola } = useLedgerUpload(null, pisos);
  const { over, dropHandlers } = useSoltar(canManage, subir);

  return (
    <div className={[styles.card, styles.cardMulti, over ? styles.cardOver : ''].filter(Boolean).join(' ')} {...dropHandlers}>
      {canManage && (
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={LEDGER_ACCEPT}
          className={styles.input}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            subir(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
      )}
      <button
        type="button"
        className={styles.face}
        onClick={() => inputRef.current?.click()}
        disabled={!canManage}
        aria-label="Subir facturas de varios pisos"
        title="El piso de cada factura sale del nombre del fichero («JG20-2 luz.pdf») o de la propia factura: su CUPS o su dirección de suministro."
      >
        <span className={styles.name}>Varios pisos</span>
        <span className={styles.plus} aria-hidden="true">
          <Icon name={over || enCola > 0 ? 'upload' : 'plus'} size={20} strokeWidth={2} />
        </span>
        <span className={styles.multiHint}>El piso sale del nombre del fichero o de la factura (su CUPS o su dirección).</span>
        <Pista over={over} enCola={enCola} toque="Pulsa para subir facturas" />
      </button>
    </div>
  );
}
